import React from 'react';
import { Badge } from "@shared/ui/badge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { BarChart2, CheckCircle2, Tag, Droplet, Waves, Trash2, Zap, Flame, FileText } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { useTranslation } from "react-i18next";

const SERVICE_CONFIG: Record<string, { icon: any, theme: string }> = {
  water: { icon: Droplet, theme: "blue" },
  sewer: { icon: Waves, theme: "teal" },
  waste_water: { icon: Waves, theme: "teal" },
  trash: { icon: Trash2, theme: "green" },
  electricity: { icon: Zap, theme: "purple" },
  gas: { icon: Flame, theme: "orange" },
};
const FALLBACK_CONFIG = { icon: FileText, theme: "blue" };

import FlatRate from './rateTypes/FlatRate';
import SlabBased from './rateTypes/SlabBased';
import TimeOfUse from './rateTypes/TimeOfUse';
import Seasonal from './rateTypes/Seasonal';

export function RateStructures({ planConfig }: { planConfig?: any }) {
  const { t } = useTranslation();
  const normalizedServices = React.useMemo(() => {
    if (!planConfig?.result?.rates) return [];
    
    const ratesObj = planConfig.result.rates;
    const items: any[] = [];
    
    Object.entries(ratesObj).forEach(([serviceKey, serviceData]: [string, any]) => {
      const normalizedKey = serviceKey.replace(/\s+/g, '').toLowerCase();

      const getTypeDisplay = (typeCode: string | number) => {
        const code = String(typeCode);
        if (code === '3') return t("Flat Rate");
        if (code === '4') return t("Slab Based");
        if (code === '5') return t("Time of Use");
        if (code === '6') return t("Seasonal");
        if (code === '7') return t("Fixed");
        return t("Flat Rate");
      };

      if (serviceData.conditional) {
        if (serviceData.belowRate) {
          items.push({
            id: `${normalizedKey}_below`,
            name: `${serviceKey} (${t("Below Threshold")})`,
            typeDisplay: getTypeDisplay(serviceData.belowRate.rateType),
            rate: serviceData.belowRate.rate,
            badgeLabel: `≤ ${serviceData.threshold} ${serviceData.thresholdUnit !== 'null' && serviceData.thresholdUnit !== 'NA' ? serviceData.thresholdUnit : ''}`.trim()
          });
        }
        if (serviceData.aboveRate) {
          items.push({
            id: `${normalizedKey}_above`,
            name: `${serviceKey} (${t("Above Threshold")})`,
            typeDisplay: getTypeDisplay(serviceData.aboveRate.rateType),
            rate: serviceData.aboveRate.rate,
            badgeLabel: `> ${serviceData.threshold} ${serviceData.thresholdUnit !== 'null' && serviceData.thresholdUnit !== 'NA' ? serviceData.thresholdUnit : ''}`.trim()
          });
        }
      } else {
        items.push({
          id: normalizedKey,
          name: serviceKey,
          typeDisplay: getTypeDisplay(serviceData.rateType),
          rate: serviceData.rate,
          badgeLabel: t("Standard Rate")
        });
      }
    });
    
    return items;
  }, [planConfig, t]);

  if (normalizedServices.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <BarChart2 className="w-4 h-4 text-gray-400" />
        <h3 className="text-xs font-bold text-gray-500">{t("Rate Structures")}</h3>
        <div className="h-px bg-gray-200 flex-1 ml-2"></div>
      </div>

      <Accordion type="multiple" defaultValue={normalizedServices.map((s, idx) => `${s.id}-${idx}`)} className="space-y-4">
        {normalizedServices.map((service, index) => {
          const configId = service.id?.toLowerCase().split('_')[0];
          const config = (configId ? SERVICE_CONFIG[configId] : undefined) || SERVICE_CONFIG[service.name?.toLowerCase()] || FALLBACK_CONFIG;
          const Icon = service.icon || config.icon;
          const currentTheme = service.theme || config.theme;
          
          const themeClasses: Record<string, any> = {
            blue: {
              item: "border-blue-100 [&[data-state=open]]:border-blue-200",
              trigger: "bg-blue-50/60 hover:bg-blue-50/90 data-[state=open]:bg-blue-50 [&>svg]:text-blue-400",
              icon: "text-blue-500",
              title: "text-blue-900",
              subtitle: "text-blue-600/80",
              badge: "bg-blue-100/50 text-blue-700 hover:bg-blue-100/50",
              content: "border-blue-100"
            },
            teal: {
              item: "border-teal-100 [&[data-state=open]]:border-teal-200",
              trigger: "bg-teal-50/60 hover:bg-teal-50/90 data-[state=open]:bg-teal-50 [&>svg]:text-teal-400",
              icon: "text-teal-600",
              title: "text-teal-900",
              subtitle: "text-teal-700/80",
              badge: "bg-teal-100/60 text-teal-700 hover:bg-teal-100/60",
              content: "border-teal-100"
            },
            green: {
              item: "border-green-100 [&[data-state=open]]:border-green-200",
              trigger: "bg-green-50/60 hover:bg-green-50/90 data-[state=open]:bg-green-50 [&>svg]:text-green-400",
              icon: "text-green-600",
              title: "text-green-900",
              subtitle: "text-green-700/80",
              badge: "bg-green-100/60 text-green-700 hover:bg-green-100/60",
              content: "border-green-100"
            },
            purple: {
              item: "border-purple-100 [&[data-state=open]]:border-purple-200",
              trigger: "bg-purple-50/60 hover:bg-purple-50/90 data-[state=open]:bg-purple-50 [&>svg]:text-purple-400",
              icon: "text-purple-600",
              title: "text-purple-900",
              subtitle: "text-purple-700/80",
              badge: "bg-purple-100/60 text-purple-700 hover:bg-purple-100/60",
              content: "border-purple-100"
            },
            orange: {
              item: "border-orange-100 [&[data-state=open]]:border-orange-200",
              trigger: "bg-orange-50/60 hover:bg-orange-50/90 data-[state=open]:bg-orange-50 [&>svg]:text-orange-400",
              icon: "text-orange-600",
              title: "text-orange-900",
              subtitle: "text-orange-700/80",
              badge: "bg-orange-100/60 text-orange-700 hover:bg-orange-100/60",
              content: "border-orange-100"
            }
          };

          const tc = themeClasses[currentTheme] || themeClasses.blue;

          let content = null;
          if (service.typeDisplay === t("Time of Use") || service.basisType == "Time Of Use" ) {
            content = <TimeOfUse slots={(service.rate || []) as any[]} />;
          } else if (service.typeDisplay === t("Slab Based") || service.basisType == "Slab" || service.basisType == "Tiered") {
            content = <SlabBased slabs={(service.rate || []) as any[]} />;
          } else if (service.typeDisplay === t("Seasonal") || service.basisType == "Seasonal") {
            content = <Seasonal seasons={(service.rate || []) as any[]} />;
          } else {
            content = <FlatRate data={service.rate || []} />;
          }

          const displayName = service.name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());

          return (
            <AccordionItem key={`${service.id}-${index}`} value={`${service.id}-${index}`} className={cn("rounded-xl overflow-hidden bg-white shadow-sm transition-colors border", tc.item)}>
              <AccordionTrigger className={cn("hover:no-underline px-5 py-4 transition-colors", tc.trigger)}>
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <Icon className={cn("w-5 h-5 shrink-0", tc.icon)} />
                    <div className="text-left">
                      <p className={cn("font-bold text-[15px]", tc.title)}>{displayName}</p>
                      <p className={cn("text-xs font-medium", tc.subtitle)}>{service.typeDisplay}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className={cn("border-0 font-semibold text-[10px] tracking-wide rounded-md px-2.5 py-1", tc.badge)}>
                    {service.badgeLabel}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className={cn("px-5 py-6 border-t", tc.content)}>
                {content}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

export default RateStructures;
